/**
 * InputText Component - Constants
 * ===============================
 */

import { getIcon } from '../../icons';

export type InputTextLabelPosition = 'top' | 'inline';
export type InputTextSize = 'sm' | 'md';
// type del <input> nativo. Excluye 'password' (ese es ec-input-password).
export type InputTextType = 'text' | 'email' | 'tel' | 'url' | 'search';
export type InputTextIconType =
  | 'none' | 'person' | 'home' | 'search' | 'mail' | 'lock' | 'calendar' | 'phone';

export interface InputTextSizeStyles {
  control: string;
  text: string;
  icon: string;
}

export interface InputTextDefaultValues {
  labelPosition: InputTextLabelPosition;
  size: InputTextSize;
  icon: InputTextIconType;
  type: InputTextType;
}

// Los tamaños md/sm mapean a L/M de Figma: solo cambia el padding del campo.
// Texto (16px) e ícono (24px) son fijos en ambos tamaños por spec.
export const SIZES: Record<InputTextSize, InputTextSizeStyles> = {
  sm: { control: 'ec-px-3 ec-py-2', text: 'ec-text-base', icon: 'ec-w-6 ec-h-6' },
  md: { control: 'ec-px-4 ec-py-3', text: 'ec-text-base', icon: 'ec-w-6 ec-h-6' }
};

export const BASE_CLASSES = {
  control:
    'ec-flex ec-items-center ec-gap-2 ec-bg-color-surface ' +
    'ec-rounded-lg ec-transition-colors focus-within:ec-ring-1 focus-within:ec-ring-color-primary',
  controlDisabled: 'ec-bg-color-bg ec-cursor-not-allowed',
  input:
    'ec-flex-1 ec-w-full ec-bg-transparent ec-outline-none placeholder:ec-text-color-text-muted',
  // Colores del texto del input: mutuamente excluyentes (nunca combinar dos).
  inputColor: 'ec-text-color-text',
  inputError: 'ec-text-color-text-danger',
  inputDisabled: 'ec-text-color-text-muted ec-cursor-not-allowed',
  label: 'ec-text-base ec-text-color-text-secondary',
  labelInline: 'ec-whitespace-nowrap',
  help: 'ec-text-caption ec-font-medium',
  // Colores del help: mutuamente excluyentes.
  helpColor: 'ec-text-color-text-secondary',
  helpError: 'ec-text-color-text-danger',
  icon: 'ec-text-color-text-muted ec-flex-shrink-0'
};

/**
 * Iconos SVG predefinidos
 *
 * Las keys públicas (icon="person", etc.) se mantienen igual — cero breaking.
 * Los valores vienen del registro central (`src/icons`); `lock` no tiene
 * equivalente en Figma y usa su SVG legacy vía `getIcon()`.
 */
export const ICONS: Record<InputTextIconType, string> = {
  none: '',
  person: getIcon('user') || '',
  home: getIcon('home') || '',
  search: getIcon('search') || '',
  mail: getIcon('email') || '',
  lock: getIcon('lock') || '',
  calendar: getIcon('calendar-empty') || '',
  phone: getIcon('call') || ''
};

export const DEFAULTS: InputTextDefaultValues = {
  labelPosition: 'top',
  size: 'md',
  icon: 'none',
  type: 'text'
};

// Whitelist para enforcear InputTextType en runtime: el binding es `@` (string),
// así que la unión TS no protege a los consumidores HTML — un type inválido
// (p. ej. 'password') pasaría derecho al DOM. Se valida en el controller.
export const VALID_TYPES: InputTextType[] = ['text', 'email', 'tel', 'url', 'search'];
